import { LoginComponent } from '@/auth/login/login.component';
import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';

// Reference: https://medium.com/javascript-in-plain-english/angular-route-reuse-strategy-b5d40adce841
// Reference: https://github.com/angular/angular/issues/25521

interface RouteCacheRecord {
    handle: DetachedRouteHandle;
    /*
     * For unclear reasons, when the navigation starts, "retrieve" is called
     * without calling "shouldAttach" first (from "createRouterState").
     * This flag is used to ignore those calls. :-)
     */
    shouldAttachCalled: boolean;
}
export class TSRouteReuseStrategy extends RouteReuseStrategy {
    handles = new Map<string, RouteCacheRecord>();
    previousRoute = ''; // Store the previous route when detaching

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const ignoreRoute = this.isToBeIgnored(route, 'shouldDetach');
        const detach = !ignoreRoute && (!!route.data.reuseRoutesFrom || false);
        // console.log(
        //     'CRRS.shouldDetach Called for',
        //     this.routeIdentifier(route),
        //     ignoreRoute,
        //     detach
        // );
        this.previousRoute = this.routeIdentifier(route);
        return detach;
    }
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        // console.log('CRRS.store called for', this.routeIdentifier(route));
        if (!handle || this.isToBeIgnored(route, 'store')) {
            return;
        }
        const routeKey = this.routeIdentifier(route);
        if (routeKey && route.data && route.data.reuseRoutesFrom) {
            const previousStored = this.handles[routeKey];
            if (previousStored) {
                // console.log('CRRS.store clearing previousStored');
                if (!(previousStored.handle.componentRef === (handle as any).componentRef)) {
                    previousStored.handle.componentRef.destroy();
                }
            }
            this.handles[routeKey] = { handle, shouldAttachCalled: false };
            const detachHook = handle ? (handle as any).componentRef.instance.ngOnDetach : null;
            if (detachHook && typeof detachHook === 'function') {
                (handle as any).componentRef.instance.ngOnDetach();
            }
        }
    }
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (route.component === LoginComponent) {
            // console.log('Login Component ==> Clearing Pre-existing Handles!', this.routeIdentifier(route), route);
            Object.keys(this.handles).forEach(rKey => {
                const storedHandle = this.handles[rKey].handle;
                if (storedHandle && !storedHandle.componentRef.hostView.destroyed) {
                    storedHandle.componentRef.destroy();
                }
            });
            this.handles = new Map<string, RouteCacheRecord>();
            return false;
        }
        const routeKey = this.routeIdentifier(route);
        const stored = this.handles[routeKey];
        const handle = stored && stored.handle;
        const destroyed = !!(handle && handle.componentRef.hostView.destroyed);
        const reuse =
            route.data && route.data.reuseRoutesFrom
                ? route.data.reuseRoutesFrom.includes(this.previousRoute)
                : false;
        // console.log('CRRS.shouldAttach: Reuse Component:', routeKey, reuse, route.data);
        const attach = !this.isToBeIgnored(route, 'shouldAttach') && handle && reuse && !destroyed;
        const reload = history.state && !!history.state.reload;
        const shouldAttach = attach && !reload;
        if (shouldAttach) {
            stored.shouldAttachCalled = true;
            // console.log('CRRS.shouldAttach', this.routeIdentifier(route), 'Need to attach');
        }
        if (!shouldAttach && stored && stored.shouldAttachCalled) {
            // console.log('CRRS.shouldAttach', this.routeIdentifier(route), 'Need to clear handle', handle);
            const previousStored = this.handles[routeKey];
            if (previousStored && previousStored.handle.componentRef) {
                previousStored.handle.componentRef.destroy();
            }
            delete this.handles[routeKey];
        }
        return shouldAttach;
    }
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
        const routeKey = this.routeIdentifier(route);
        const stored = this.handles[routeKey];
        const handle = stored ? stored.handle : null;
        const destroyed = !!(handle && handle.componentRef.hostView.destroyed);
        const shouldBeRetrieved = !destroyed && stored && stored.shouldAttachCalled;
        if (destroyed) {
            this.handles.delete(routeKey);
        }
        if (shouldBeRetrieved) {
            // console.log('CRRS.retrieve - Should be retrieved == ', routeKey, handle);
            stored.shouldAttachCalled = false;
            if (this.isToBeIgnored(route, 'retrieve')) {
                return null;
            }
            const attachHook = handle ? (handle as any).componentRef.instance.ngOnAttach : null;
            if (attachHook && typeof attachHook === 'function') {
                (handle as any).componentRef.instance.ngOnAttach();
            }
            return handle;
        }
        // Pre-retrieve
        return this.isToBeIgnored(route, 'retrieve') ? null : handle;
    }

    // Called when the route transition source and destination components are the same!
    shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
        const defaultReuse = future.routeConfig === current.routeConfig;
        const customReuse =
            future.data && future.data.reuseRoutesFrom
                ? future.data.reuseRoutesFrom.includes(this.routeIdentifier(current))
                : true;
        const className =
            (future.routeConfig &&
                future.routeConfig.component &&
                future.routeConfig.component.name) ||
            '';
        // console.log(
        //     'CRRS.shouldReuseRoute Called for',
        //     `${this.routeIdentifier(current)} to ${this.routeIdentifier(future)}`,
        //     `${future.routeConfig?.path || 'None'} to ${current.routeConfig?.path || 'None'}`,
        //     `${defaultReuse} || ${customReuse}`
        // );
        // Dont Reuse Route for TemplateComponent
        return className === 'TemplateComponent' ? false : defaultReuse && customReuse;
    }

    private routeIdentifier(route: ActivatedRouteSnapshot) {
        // Build the complete path from the root to the input route. More reliable than using routeConfig.path
        return (
            '/' +
            route.pathFromRoot
                .map((snapshot: ActivatedRouteSnapshot) =>
                    snapshot.routeConfig ? snapshot.routeConfig.path : ''
                )
                .filter((path: string) => path.length > 0)
                .join('/')
        );
    }

    private isToBeIgnored(route: ActivatedRouteSnapshot, _msg: string) {
        if (!route.routeConfig) {
            return true;
        }
        if (route.routeConfig.loadChildren || route.children.length) {
            return true;
        }
        return false;
    }
}
